import React, {
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import moment from "moment";
import { useParams } from "react-router";
import { Loading, useRedirect } from "react-admin";
import { BiEdit, BiSearch, BiTrash, BiX } from "react-icons/bi";
import { GoDownload } from "react-icons/go";
import { routePaths } from "../../baseLayout/SidebarMenu";
import { RiComputerLine, RiGroupLine } from "react-icons/ri";

import {
  IButtonActions,
  IFdListHeaderAction,
} from "../../dataProvider/interfaces/IEvent";
import { StoreContext } from "../../store/Store";
import { APIContext } from "../../store/APIStore";

import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";

import FdListHeaders from "../../components/GeneralList/FdListHeaders";
import FdListItem from "../../components/GeneralList/item";
import FdList from "../../components/GeneralList/list";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";

import FdButton from "../../components/UIComponents/Button/FdButton";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";

import { LeadRetrieval as classes } from "./styles";
import { Wrapper } from "../../CommonStyles";
import { IExhibitor } from "../../dataProvider/interfaces/ILeads";
import { theme } from "../../theme";
import { actionDialogType, SimpleActionDialog } from "../../components";
import { Props as dialogType1 } from "../../components/SimpleActionDialog/SimpleActionDialog";
import { useHasLead, useHasPermissions } from "../../hooks/useHasPermissions";
import IDeveloperAccount from "../../dataProvider/interfaces/IDeveloperAccount";
import { PERMISSIONS } from "../../roles";
import WindowSidebarIcon from "../../Svg/WindowSidebar";
import { profileNames } from "../../dataProvider/interfaces/IUser";

const downloadXlsxFromLink = (url: string) => {
  if (url) {
    const link = document.createElement("a");
    link.href = url;
    link.click();
  }
};
type dialogType = {
  open: boolean;
  cancelLabel: string;
  icon: ReactElement | undefined;
  message: string;
  type: actionDialogType;
  isLoading: boolean;
  actionCallback: Function | undefined;
};

const EmptyScreen = ({
  message,
  handleFunc,
  buttonName,
  icon,
}: {
  message?: string;
  handleFunc?: Function;
  buttonName?: string;
  icon?: ReactNode;
}) => (
  <Box sx={classes.emptyScreen}>
    <Box sx={{ textAlign: "center" }}>
      {icon && <Box className="iconStyle">{icon}</Box>}
      <Box>
        <FdTypography label={message} />
      </Box>
      {buttonName && (
        <Box sx={{ mt: 2 }}>
          <FdButton
            ButtonName={buttonName}
            onClick={() => {
              if (handleFunc) handleFunc();
            }}
          />
        </Box>
      )}
    </Box>
  </Box>
);

const ExhibitorList = () => {
  const { eventId } = useParams() as any;
  const [loading, setLoading] = useState<boolean>(false); // data loading flag
  const [checkedItems, setCheckedItems] = useState<Array<number>>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [devInfo, setDevInfo] = useState<IDeveloperAccount | null>(null);
  const [exhibitorsCount, setExhibitorsCount] = useState(0);
  const [exhibitors, setExhibitors] = useState<IExhibitor[]>([]);
  const [state, dispatch] = useContext(StoreContext);
  const redirect = useRedirect();
  const api = useContext(APIContext);
  const notificationRef = useRef<any>();
  const canManageLeads = useHasPermissions(PERMISSIONS.CAN_MANAGE_LEADS);
  const { fetchLeadEventData } = useHasLead(state.selectedEvent?.id);
  const [confirmationDialog, setConfirmationDialog] = useState<dialogType1>({
    open: false,
    message: "",
    cancelLabel: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
  } as dialogType);
  const RETRY_COUNT: number = 10;

  const getPrimaryName = (item: IExhibitor): string => {
    return item.name;
  };
  const getSecondaryName = (item: IExhibitor): React.ReactNode => {
    return (
      <>
        <Box sx={{ mr: 1 }} component="span">
          {item.username}
        </Box>
        <Box sx={{ mr: 1, color: "#000" }} component="span">
          {item.organisation.name}
        </Box>
        <Box component="span">{item.email}</Box>
      </>
    );
  };
  const fetchDeveloperAccount = async () => {
    try {
      const accounts = await api.developerAccountService.getDeveloperAccount(
        String(state?.currentUser.id)
      );
      // there should be only one dev account for every user
      if (accounts && accounts[0]) {
        setDevInfo(accounts[0]);
      } else {
        setDevInfo(null);
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  useEffect(() => {
    if (state?.currentUser && canManageLeads) {
      fetchDeveloperAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCheckItem = (id: any) => {
    const currentIndex = checkedItems.indexOf(id);
    const newChecked = [...checkedItems];
    if (currentIndex === -1) {
      newChecked.push(id);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setCheckedItems(newChecked);
  };

  const handleBulkDelete = async (items: number[]) => {
    const payload = {
      exhibitorIds: items,
    };
    try {
      setCurrentPage(-1);
      const bulkDelete = (await api.leadsService.deleteExhibitors(
        payload
      )) as any;
      handleDialogClose();
      fetchExhibitor();
      notificationRef.current?.show(
        bulkDelete.message.success,
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  // actions applied on selected items
  const bulkActions: Array<IFdListHeaderAction> = [
    {
      label: "Delete",
      key: "delete",
      callback: () => showDeleteDialog(),
      startIcon: <BiTrash />,
      position: "LEFT",
      isEnabled: Boolean(checkedItems.length > 0),
    },
  ];

  const showDeleteDialog = () => {
    setConfirmationDialog({
      open: true,
      title: `Are you sure you want to delete selected exhibitor${
        checkedItems.length > 1 ? "s" : ""
      }?`,
      icon: <BiTrash size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      onConfirm: () => {
        handleBulkDelete(checkedItems);
      },
      onCancel: handleDialogClose,
    });
  };

  useEffect(() => {
    // fetch the selected event if it wasn't set in the store
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state?.leadsData?.leadsInfo && !state.leadsData?.loading) {
      fetchExhibitor();
    }
  }, [state?.leadsData]);

  const handleEnableLeads = async () => {
    const promises: any = [];
    const leadsCall = async () => {
      // setCurrentPage(-1);
      let apiKey = "";
      const userPayload = {
        fullName: `Lead Retrieval ${state.selectedEvent?.id}`,
        username: `lr_user_${state.selectedEvent?.id}`,
        password: Math.random().toString(36).slice(2, 10),
        profile: profileNames.VIEWER,
        account: state.selectedEvent?.account?.id,
        email: "",
      };
      userPayload.email = `${userPayload.username}@fielddrive.com`;
      const callUserList = (await api.accountService.getAccountsUsers(
        state.selectedEvent?.account?.id
      )) as any;
      const hasUserName = callUserList.find(
        (el: any) => el.username === userPayload.username
      );
      if (Boolean(hasUserName)) {
        const accounts = (await api.developerAccountService.getDeveloperAccount(
          String(hasUserName?.id)
        )) as any;
        if (accounts && accounts[0]) {
          apiKey = accounts?.[0]?.apiKey;
        }
      } else {
        const newUser = (await api.userService.create(userPayload)) as any;
        const accounts = (await api.developerAccountService.getDeveloperAccount(
          String(newUser?.id)
        )) as any;
        if (accounts && accounts[0]) {
          apiKey = accounts?.[0]?.apiKey;
        }
      }
      const leadEventPayload = {
        eventId: state?.selectedEvent?.id,
        name: state?.selectedEvent?.name,
        startDate: moment(state?.selectedEvent?.startDateTime).format(
          "YYYY-MM-DDTHH:mm:ss[Z]"
        ),
        endDate: moment(state?.selectedEvent?.endDateTime).format(
          "YYYY-MM-DDTHH:mm:ss[Z]"
        ),
        location: state?.selectedEvent?.locationName,
        timezone: state?.selectedEvent?.timeZone,
        appConfig: {
          apiKey: apiKey,
          archivalDays: state?.selectedEvent?.archivingPeriod,
          dailyReportsEnabled: true,
          gdprConfig: {
            approveButtonLabel: "",
            cancelButtonLabel: "",
            title: "",
            description: "",
            gdprConsent: false,
          },
        },
      };
      await api.leadsService.enableEvent(leadEventPayload);
      dispatch({ type: "SET_HAS_LEAD", payload: !state?.leadsData?.leadsInfo });
      await fetchLeadEventData();
      setCurrentPage(1);
      notificationRef.current?.show(
        "Leads successfully enabled",
        notificationType.Success
      );
    };

    promises.push(leadsCall());

    try {
      setIsPageLoading(true);
      await Promise.all(promises);
      setIsPageLoading(false);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data?.message,
        notificationType.Error
      );
    }
  };

  const fetchExhibitor = async (searchKey = "", page: number = 1) => {
    const params = {
      eventId: eventId,
      page: page,
      limit: 50,
      search: searchKey,
    };
    try {
      setLoading(true);
      setIsPageLoading(true);
      const exhibitorData = await api.leadsService.getExhibitors({ ...params });
      const { count, exhibitors: exhibitorList } = exhibitorData?.data;
      if (page > 1) {
        setExhibitors([...exhibitors, ...exhibitorList]);
      } else {
        setExhibitors(exhibitorList);
      }
      setExhibitorsCount(count);
      setCurrentPage(page);
      setCheckedItems([]);
      setLoading(false);
      setIsPageLoading(false);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data?.message,
        notificationType.Error
      );
    }
  };
  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  const getButtonActions = (item: IExhibitor): Array<IButtonActions> => [
    {
      type: "label",
      label: (
        <Box sx={{ mr: 0.5 }}>
          {item.leadsCount || 0} lead
          {item.leadsCount && item.leadsCount <= 1 ? "" : "s"}
        </Box>
      ),
    },
    {
      label: "edit",
      icon: <BiEdit size="16" />,
      type: "edit",
      callback: () => {
        redirect(
          `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.EXHIBITOR}/${item.exhibitorId}/${routePaths.EDIT}`
        );
      },
    },
  ];

  //search

  const handleListPagination = () => {
    if (exhibitors.length < exhibitorsCount) {
      fetchExhibitor(searchKey, currentPage + 1);
    }
  };

  const onSearchClick = () => {
    if (searchKey !== "") {
      setCurrentPage(-1);
      redirect(
        `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.EXHIBITOR}?search=${searchKey}`
      );
      fetchExhibitor(searchKey);
    }
  };

  const handleSearchClear = () => {
    setCurrentPage(-1);
    redirect(
      `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.EXHIBITOR}`
    );
    setSearchKey("");
    fetchExhibitor();
  };

  const handleSearchKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      setCurrentPage(-1);
      redirect(
        `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.EXHIBITOR}?search=${searchKey}`
      );
      fetchExhibitor(searchKey);
    }
  };

  // Function to handle errors and close dialog
  const handleError = (error: any) => {
    const message = error?.response?.data?.message || error.message;
    console.error(message);
    setConfirmationDialog({ ...confirmationDialog, open: false });
    notificationRef.current?.show(message, notificationType.Error);
  };

  const fetchDownloadData = async () => {
    try {
      setConfirmationDialog({
        ...confirmationDialog,
        open: true,
        title: "Generating report. This can take a few minutes.",
        isLoading: true,
        icon: <></>,
      });
      const resp: any = await api.leadsService.generateReport(
        "exhibitorAccountLeadsCountReport",
        eventId
      );
      if (resp?.data?.reportId) {
        await checkReportStatus(resp.data.reportId, RETRY_COUNT);
      } else {
        handleError(new Error("Failed to generate report, please try again."));
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const checkReportStatus = async (
    reportId: number,
    retries: number
  ): Promise<void> => {
    try {
      const resp: any = await api.leadsService.getReport(reportId);
      const { status } = resp.data || {};

      if (status === "READY") {
        await downloadReport(reportId);
      } else {
        if (retries > 0) {
          setTimeout(() => {}, 1000 * (retries * (RETRY_COUNT - retries) + 1)); // wait before retrying 3sec, 4sec then 6sec
          await checkReportStatus(reportId, retries - 1);
        } else {
          throw new Error("Failed to generate report, please try again.");
        }
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const downloadReport = async (reportId: number) => {
    try {
      let downloadLink: any = await api.leadsService.downloadReport(reportId);
      if (downloadLink?.data?.downloadUrl) {
        downloadXlsxFromLink(downloadLink.data.downloadUrl);
        setConfirmationDialog({
          ...confirmationDialog,
          open: false,
          cancelLabel: "",
        });
        notificationRef.current?.show(
          "Report has been generated successfully",
          notificationType.Success
        );
      } else {
        handleError(new Error("Failed to download report, please try again."));
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  return (
    <Box sx={classes.wrapper}>
      <Wrapper>
        <Box sx={classes.topActions}>
          <Box sx={classes.subDiv}>
            <FdTypography variant="h3" label="Exhibitors" />
            {state?.leadsData?.leadsInfo && (
              <FdTypography
                variant="subtitle1"
                style={{ lineHeight: 1, fontSize: "14px", color: "#787878" }}
                label={
                  <>
                    {" "}
                    <RiGroupLine
                      style={{
                        position: "relative",
                        top: "3px",
                        marginRight: "7px",
                        color: theme.palette.text.primary,
                        fontSize: "18px",
                      }}
                    />{" "}
                    Total exhibitors:{" "}
                    <Box component="span" fontWeight="bold">
                      {exhibitorsCount}
                    </Box>{" "}
                  </>
                }
              />
            )}
          </Box>
          {state?.leadsData?.leadsInfo && (
            <Box textAlign="right" sx={classes.subDiv}>
              <FdButton
                startIcon={<GoDownload />}
                ButtonName="Download list"
                variant="text"
                onClick={fetchDownloadData}
              />

              <FdButton
                ButtonName="Add Exhibitor"
                onClick={() =>
                  redirect(
                    `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.NEW_EXHIBITOR}`
                  )
                }
              />
            </Box>
          )}
        </Box>
        <Box sx={classes.topFilters} display="flex">
          <Box sx={classes.searchForm}>
            <FdTextField
              sx={classes.searchInput}
              placeholder="Search"
              type="text"
              name="search"
              onChange={(e) => {
                const keyword = e.target.value;
                setSearchKey(keyword);
              }}
              value={searchKey}
              autoFocus
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BiSearch size="18" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={classes.clearIcon}
                    onClick={handleSearchClear}
                  >
                    {searchKey.length > 0 && <BiX size="20" />}
                  </InputAdornment>
                ),
              }}
            />

            <FdButton
              variant="contained"
              color="primary"
              onClick={onSearchClick}
              sx={classes.searchButton}
              ButtonName="Search"
            />
          </Box>
        </Box>
        <Box sx={classes.listContent}>
          {isPageLoading || state?.leadsData?.loading ? (
            <Box sx={classes.loadingBox}>
              <Loading />
            </Box>
          ) : (
            <Box height="100%">
              {exhibitors.length !== 0 && (
                <FdListHeaders actions={bulkActions} />
              )}
              <FdList
                className="set-height"
                items={exhibitors}
                isLoading={loading}
                onBottomScroll={handleListPagination}
                noData={
                  !state?.leadsData?.leadsInfo && !state?.leadsData?.loading ? (
                    <EmptyScreen
                      buttonName="Enable"
                      message="Enable Lead retrieval dashboard for this event"
                      handleFunc={() => handleEnableLeads()}
                      icon={<WindowSidebarIcon />}
                    />
                  ) : exhibitors.length === 0 ? (
                    <EmptyScreen
                      message="No exhibitors have been added"
                      icon={
                        <RiComputerLine
                          size="60px"
                          color={theme.palette.primary.main}
                        />
                      }
                    />
                  ) : (
                    ""
                  )
                }
              >
                {exhibitors?.map((item: IExhibitor) => (
                  <FdListItem
                    listKey={item.exhibitorId}
                    id={item.exhibitorId}
                    label={getPrimaryName(item)}
                    description={getSecondaryName(item)}
                    onSelectItem={() => {
                      redirect(
                        `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.EXHIBITOR}/${item.exhibitorId}/${routePaths.EDIT}`
                      );
                    }}
                    buttonActions={getButtonActions(item)}
                    onCheckedItem={onCheckItem}
                    isChecked={checkedItems.includes(Number(item.exhibitorId))}
                  />
                ))}
              </FdList>
            </Box>
          )}
        </Box>
      </Wrapper>

      <SimpleActionDialog
        type={confirmationDialog.type}
        icon={confirmationDialog.icon}
        open={confirmationDialog.open}
        title={confirmationDialog.title}
        isLoading={confirmationDialog.isLoading}
        onConfirm={confirmationDialog.onConfirm as Function}
        cancelLabel={confirmationDialog.cancelLabel}
        onCancel={handleDialogClose}
      />
      <NotificationMessage ref={notificationRef} />
    </Box>
  );
};

export default ExhibitorList;
